// (c) chaehongee. Confidential and proprietary.

import { useEffect, useRef } from 'react'
import { addItems as addCanvasItems, removeItem as removeCanvasItem, setItems as setCanvasItems, clearItems as clearCanvasItems } from '../../../../store/slices/canvasSlice'

interface NodeLike {
    id: string
    image_path?: string
    type?: string
}

interface SessionLike {
    id: string
    nodes: NodeLike[]
}

export function useAutoAddCanvasItems(params: {
    session: SessionLike | null
    canvasItems: Array<{ id: string; src: string; mediaType?: string }>
    dispatch: (action: any) => void
    hiddenIds: Set<string>
    preloadMediaSizes?: (items: Array<{ src: string; mediaType?: string }>) => Promise<void>
}): void {
    const { session, canvasItems, dispatch, hiddenIds, preloadMediaSizes } = params
    const prevSessionIdRef = useRef<string | null>(null)
    const prevNodeIdsRef = useRef<string[]>([])
    const canvasItemsRef = useRef(canvasItems)
    const initialSyncDoneRef = useRef<boolean>(false)

    // Keep ref in sync
    useEffect(() => {
        canvasItemsRef.current = canvasItems
    }, [canvasItems])

    useEffect(() => {
        let cancelled = false

        const sid = session?.id ?? null
        const nodes = session?.nodes ?? []
        const currentIds = nodes.map((n) => n.id)

        const run = async () => {
            // Reset tracking when session changes
            if (sid !== prevSessionIdRef.current) {
                prevSessionIdRef.current = sid
                prevNodeIdsRef.current = []
                initialSyncDoneRef.current = false
            }

            // If we haven't completed the initial sync yet for this session,
            // keep attempting a full sync (cancellation-safe).
            if (!initialSyncDoneRef.current) {
                const newItems = nodes
                    .filter((n) => typeof n.image_path === 'string' && n.image_path)
                    // Masks are edit scaffolding, not deliverables — never place
                    // the raw black/white blob on the canvas.
                    .filter((n) => n.type !== 'mask')
                    .filter((n) => !hiddenIds.has(n.id))
                    .map((n) => ({
                        id: n.id,
                        src: n.image_path as string,
                        mediaType: n.type ?? 'image',
                    }))

                if (preloadMediaSizes && newItems.length > 0) {
                    await preloadMediaSizes(
                        newItems.map((it) => ({
                            src: it.src,
                            mediaType: it.mediaType,
                        }))
                    )
                }
                if (cancelled) return

                if (sid) {
                    if (newItems.length > 0) {
                        dispatch(setCanvasItems({ sessionId: sid, items: newItems }))
                    } else {
                        dispatch(clearCanvasItems({ sessionId: sid }))
                    }
                }

                prevNodeIdsRef.current = currentIds
                initialSyncDoneRef.current = true
                return
            }

            // Detect new nodes by id diff
            const prevIds = prevNodeIdsRef.current
            const addedIds = currentIds.filter((id) => !prevIds.includes(id))
            const removedIds = prevIds.filter((id) => !currentIds.includes(id))

            const currentCanvasItems = canvasItemsRef.current
            const canvasIdSet = new Set(currentCanvasItems.map((it) => it.id))

            // Remove canvas items for nodes that no longer exist (sync)
            if (removedIds.length > 0) {
                for (const rid of removedIds) {
                    if (canvasIdSet.has(rid)) {
                        if (sid) dispatch(removeCanvasItem({ sessionId: sid, itemId: rid }))
                    }
                }
            }

            // Add newly created nodes that have images (async preload, then dispatch)
            if (addedIds.length > 0) {
                const newItems = nodes
                    .filter((n) => addedIds.includes(n.id))
                    .filter((n) => typeof n.image_path === 'string' && n.image_path)
                    .filter((n) => n.type !== 'mask')
                    .filter((n) => !hiddenIds.has(n.id))
                    .filter((n) => !canvasIdSet.has(n.id))
                    .map((n) => ({
                        id: n.id,
                        src: n.image_path as string,
                        mediaType: n.type ?? 'image',
                    }))
                if (newItems.length > 0) {
                    if (preloadMediaSizes) {
                        await preloadMediaSizes(
                            newItems.map((it) => ({
                                src: it.src,
                                mediaType: it.mediaType,
                            }))
                        )
                    }
                    if (cancelled) return
                    if (sid) dispatch(addCanvasItems({ sessionId: sid, items: newItems }))
                }
            }

            // Only update prev tracking if we successfully completed this run.
            prevNodeIdsRef.current = currentIds
        }

        void run()
        return () => { cancelled = true }
    }, [session?.nodes, session?.id, dispatch, hiddenIds, preloadMediaSizes])
}

export default useAutoAddCanvasItems
